import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

const DROPDOWN_STYLE = `
  position:absolute;top:calc(100% + 6px);left:0;z-index:200;
  background:var(--color-surface);border:1px solid var(--color-border);
  border-radius:10px;box-shadow:0 4px 16px rgba(0,0,0,.1);
  padding:6px;min-width:170px;
`;

const OPTION_BASE = `
  display:flex;align-items:center;gap:10px;
  padding:8px 10px;border-radius:6px;cursor:pointer;
  font-size:14px;color:var(--color-text);
`;

function Chevron({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
      style={`flex-shrink:0;transition:transform .15s;transform:${open ? 'rotate(180deg)' : 'rotate(0)'}`}>
      <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  );
}

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return { open, setOpen, ref };
}

function TriggerButton({ label, isActive, open, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={`
        display:inline-flex;align-items:center;gap:8px;
        padding:8px 12px;border-radius:8px;font-size:14px;
        font-family:var(--font-body);cursor:pointer;
        background:var(--color-surface);color:var(--color-text);
        border:1px solid ${isActive ? 'var(--color-primary)' : 'var(--color-border)'};
        width:170px;justify-content:space-between;
        transition:border-color .15s;
      `}
    >
      <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{label}</span>
      <Chevron open={open} />
    </button>
  );
}

// Single-select custom dropdown — same visual as MultiFilterSelect
export function FilterSelect({ label, value, options, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const selected = options.find(o => o.value === value);
  const buttonLabel = selected ? selected.label : label;
  const isActive = !!value;

  return (
    <div style="position:relative;display:inline-block" ref={ref}>
      <TriggerButton label={buttonLabel} isActive={isActive} open={open} onClick={() => setOpen(o => !o)} />
      {open && (
        <div style={DROPDOWN_STYLE}>
          <div
            style={`${OPTION_BASE}${!value ? 'font-weight:600' : ''}`}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {label}
          </div>
          {options.map(opt => (
            <div
              key={opt.value}
              style={`${OPTION_BASE}${opt.value === value ? 'font-weight:600;color:var(--color-primary)' : ''}`}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Multi-select dropdown — stays open while picking checkboxes
export function MultiFilterSelect({ label, values, options, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const isActive = values.length > 0;
  const buttonLabel = isActive
    ? values.map(v => options.find(o => o.value === v)?.label ?? v).join(', ')
    : label;

  const toggle = (val) => {
    onChange(values.includes(val) ? values.filter(v => v !== val) : [...values, val]);
  };

  return (
    <div style="position:relative;display:inline-block" ref={ref}>
      <TriggerButton label={buttonLabel} isActive={isActive} open={open} onClick={() => setOpen(o => !o)} />
      {open && (
        <div style={DROPDOWN_STYLE}>
          {options.map(opt => (
            <label
              key={opt.value}
              style={OPTION_BASE}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <input
                type="checkbox"
                checked={values.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                style="width:15px;height:15px;accent-color:var(--color-primary);cursor:pointer;flex-shrink:0"
              />
              {opt.label}
            </label>
          ))}
          {isActive && (
            <div style="border-top:1px solid var(--color-border);margin-top:4px;padding-top:4px">
              <div
                style={`${OPTION_BASE}font-size:13px;color:var(--color-text-muted)`}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { onChange([]); setOpen(false); }}
              >
                Wis filter
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Filters({ children }) {
  return <div class="filters-bar">{children}</div>;
}
